import {useState} from 'react';
import {AiOutlineDownload} from 'react-icons/ai';
import {FlexiButton} from '@flexinet/ui-components';

import * as PlaybooksService from '../services/PlaybooksService';
import {
    buildPlaybookDefinition,
    serializePlaybookDefinition,
} from '../utils/playbookDefinition';
import {notifyError, notifySuccess} from '../../../components/PlatformNotification';

// FNP-1011: download a playbook's definition as a .json file.
//
// The file is what makes "the same playbook in every environment" checkable
// instead of hoped-for. It carries no ids and no stateMachineArn, and its keys
// are ordered so two exports of equivalent playbooks are byte-identical — which
// is what lets dev-vs-test drift be diffed.

export const ExportRecordButton = ({
                                       playbookId,
                                       playbookName,
                                   }: {
    playbookId: string;
    // Optional because Playbook.name is optional on the type. The FILE still
    // needs a name, so the record's own name (fetched below) is authoritative
    // and this is only the button's tooltip.
    playbookName?: string;
}) => {
    const [busy, setBusy] = useState(false);

    const handleExport = async () => {
        setBusy(true);
        try {
            const record = await PlaybooksService.fetchForExport(playbookId);

            if (!record) {
                notifyError(new Error('Playbook not found.'));
                return;
            }

            if (!record.name) {
                // Every playbook has a name in practice; the type says
                // optional. Refusing beats writing a file called
                // "undefined.json" that then fails validation on import.
                notifyError(new Error('That playbook has no name and cannot be exported.'));
                return;
            }

            const definition = buildPlaybookDefinition({
                name: record.name,
                displayName: record.displayName,
                description: record.description,
                category: record.category,
                status: record.status,
                inputSchema: record.inputSchema,
                phases: record.phases ?? [],
            });

            const json = serializePlaybookDefinition(definition);

            // ⚠️ No file-download pattern existed in this app before this — the
            // one `download` attribute in the codebase points at a pre-existing
            // S3 URL and builds nothing client-side. Blob + createObjectURL is
            // the dependency-free way to hand the browser bytes we generated;
            // adding @mantine/dropzone or file-saver for one JSON file would be
            // a new dependency for no benefit.
            const blob = new Blob([json], {type: 'application/json'});
            const url = URL.createObjectURL(blob);

            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `${record.name}.json`;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);

            // Revoked or the blob leaks for the life of the document.
            URL.revokeObjectURL(url);

            const stepCount = definition.phases.reduce((n, p) => n + p.steps.length, 0);
            notifySuccess(
                `Exported ${definition.phases.length} phases and ${stepCount} steps.`,
            );
        } catch (err) {
            notifyError(err as Error);
        } finally {
            setBusy(false);
        }
    };

    return (
        <FlexiButton
            key={'export'}
            variant={'outline'}
            loading={busy}
            leftSection={<AiOutlineDownload/>}
            onClick={() => {
                void handleExport();
            }}
            title={`Download ${playbookName ?? 'this playbook'} as a JSON definition`}
        >
            Export
        </FlexiButton>
    );
};
